import { getLocale } from 'core/store/selectors/getLocale';
import { getOriginalScale, getPointsScale } from 'core/utils/constants/evaluation';
import { EMPTY_VALUE_INDICATOR, formatDisplayValue, useLocalizedInput } from 'core/utils/numericInputUtils';
import { getUserNoteStatusPercent } from 'core/utils/user';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

function toDisplayNumber(serverValue, min, max, numberDecimals) {
  if (serverValue == null) return null;
  const raw = getOriginalScale(serverValue, min, max, numberDecimals);
  if (raw === '-' || raw == null) return null;
  return parseFloat(String(raw).replace(',', '.'));
}

export default function useInputPointsNumericScale({ ariaLabel, props, max, onChange, colorsPoints, min, changeError, numberDecimals = 2 }) {
  const { t } = useTranslation();
  const locale = useSelector(getLocale);
  const formatOptions = { minimumFractionDigits: 0, maximumFractionDigits: numberDecimals };

  const displayNum = toDisplayNumber(props.value, min, max, numberDecimals);

  const { value, setValue, hasError, setHasError, onChangeText, onFocus, onBlur } = useLocalizedInput({
    initialValue: displayNum != null ? formatDisplayValue(displayNum, locale, formatOptions) : EMPTY_VALUE_INDICATOR,
    locale,
    max,
    formatOptions,
    onChange: (parsedValue) => {
      onChange?.(parsedValue !== null ? getPointsScale(parsedValue, min, max) : null);
    },
    extraValidation: min != null && min > 0 ? (parsed) => parsed >= min : undefined,
  });

  useEffect(() => {
    const num = toDisplayNumber(props.value, min, max, numberDecimals);
    setValue(num != null ? formatDisplayValue(num, locale, formatOptions) : EMPTY_VALUE_INDICATOR);
    const isValid = num == null || (num >= (min ?? 0) && num <= max);
    if (isValid) setHasError(false);
  }, [props.value, min, max, numberDecimals, locale]);

  useEffect(() => {
    changeError?.(hasError);
  }, [hasError, changeError]);

  const statusColor = useMemo(() => {
    if (colorsPoints) return getUserNoteStatusPercent(props.value, 10);
    return 'default';
  }, [props.value, colorsPoints]);

  const accessibleName = ariaLabel || t('grades:Score out of total', { total: max });

  return { accessibleName, value, hasError, onChangeText, onFocus, onBlur, statusColor, t };
}
